// components/SearchableDropdown.js
import React, { useEffect } from 'react';

const SearchableDropdown = ({
  id,
  title,
  options,
  selected,
  onSelect,
  searchable,
  darkMode,
  activeDropdown,
  setActiveDropdown,
  searchTerm,
  setSearchTerm,
  dropdownRef
}) => {
  // Получаем уникальные значения для выпадающего списка
  const getUniqueOptions = () => {
    if (!Array.isArray(options)) return [];

    return [...new Set(options.map(option => {
      switch (id) {
        case 'brand':
          return option.brand;
        case 'model':
          return option.model;
        case 'modification':
          return option.modification;
        case 'roofType':
          return option.roof_type;
        default:
          return option;
      }
    }))];
  };

  const uniqueOptions = getUniqueOptions();
  const isActive = activeDropdown === id;

  // Исправление: преобразуем option и searchTerm к строкам перед вызовом toLowerCase()
  const filteredOptions = searchable && searchTerm && isActive
    ? uniqueOptions.filter(option => {
      // Преобразуем option к строке и проверяем, что searchTerm тоже строка
      const optionStr = String(option || '');
      const searchTermStr = String(searchTerm || '');
      return optionStr.toLowerCase().includes(searchTermStr.toLowerCase());
    })
    : uniqueOptions;

  // Проверяем, что выбранное значение есть в списке опций
  useEffect(() => {
    if (selected && uniqueOptions.length > 0 && !uniqueOptions.includes(selected)) {
      // Если выбранного значения нет в списке, сбрасываем его
      onSelect('');
    }
  }, [uniqueOptions, selected, onSelect]);

  // Обработчик клавиатуры для доступности
  const handleKeyDown = (e, option) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(option);
      setActiveDropdown(null);
      setSearchTerm('');
    }
  };

  // Закрытие выпадающего списка по Escape
  const handleDropdownKeyDown = (e) => {
    if (e.key === 'Escape') {
      setActiveDropdown(null);
    }
  };

  return (
    <div className="mb-4" ref={id === activeDropdown ? dropdownRef : null}>
      <label
        id={`${id}-label`}
        className={`text-sm font-medium mb-2 block ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
      >
        {title}
      </label>
      <div className="relative">
        <div
          role="combobox"
          aria-expanded={isActive}
          aria-owns={`${id}-listbox`}
          aria-haspopup="listbox"
          aria-labelledby={`${id}-label`}
          tabIndex="0"
          className={`w-full p-3 border rounded-lg flex justify-between items-center cursor-pointer ${darkMode
            ? 'bg-gray-800 border-gray-700 text-white'
            : 'bg-white border-gray-300'
            }`}
          onClick={() => {
            setActiveDropdown(isActive ? null : id);
            setSearchTerm('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
              e.preventDefault();
              setActiveDropdown(isActive ? null : id);
              setSearchTerm('');
            }
            if (e.key === 'Escape') {
              setActiveDropdown(null);
            }
          }}
        >
          <span className={selected ? "" : "text-gray-500"}>{selected || "Выберите..."}</span>
          <span className="text-gray-500" aria-hidden="true">{isActive ? '▲' : '▼'}</span>
        </div>

        {isActive && (
          <div
            id={`${id}-listbox`}
            role="listbox"
            aria-labelledby={`${id}-label`}
            className={`absolute z-10 w-full mt-1 border rounded-lg shadow-lg max-h-60 overflow-y-auto ${darkMode
              ? 'bg-gray-800 border-gray-700 text-white'
              : 'bg-white border-gray-300'
              }`}
            onKeyDown={handleDropdownKeyDown}
          >
            {searchable && (
              <div className={`sticky top-0 p-2 border-b ${darkMode
                ? 'bg-gray-800 border-gray-700'
                : 'bg-white border-gray-300'
                }`}>
                <input
                  type="text"
                  placeholder="Поиск..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className={`w-full p-2 border rounded ${darkMode
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300'
                    }`}
                  onClick={e => e.stopPropagation()}
                  aria-controls={`${id}-listbox`}
                  autoFocus
                />
              </div>
            )}

            {filteredOptions.length > 0 ? (
              filteredOptions.map(option => (
                <div
                  key={option}
                  role="option"
                  aria-selected={selected === option}
                  tabIndex="0"
                  className={`p-3 cursor-pointer ${selected === option
                    ? darkMode ? 'bg-blue-900' : 'bg-blue-100'
                    : ''
                    } ${darkMode
                      ? 'hover:bg-gray-700'
                      : 'hover:bg-gray-100'
                    }`}
                  onClick={() => {
                    onSelect(option);
                    setActiveDropdown(null);
                    setSearchTerm('');
                  }}
                  onKeyDown={(e) => handleKeyDown(e, option)}
                >
                  {option}
                </div>
              ))
            ) : (
              <div className="p-3 text-gray-500">Ничего не найдено</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchableDropdown;